import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import InstallAppButton from "@/components/InstallAppButton";
import { Logo, KenyaStripe } from "@/components/Logo";
import SponsorInquiryDialog from "@/components/SponsorInquiryDialog";
import HighlightsReel from "@/components/HighlightsReel";
import LiveScoreTicker from "@/components/LiveScoreTicker";
import { PadelLiveScore, PadelFinalScore } from "@/components/PadelScore";
import {
  ArrowRight, ArrowUpRight, Calendar, Crown, Megaphone, Tv, UserPlus, LogIn,
  Users2, Flag, Star, Handshake, Check, Activity,
} from "lucide-react";

const HERO_IMG = "/assets/hero-padel.jpg";
const SEASON_IMG = "/assets/season-2-action.jpg";

const PAST_HIGHLIGHTS = [
  { y: "S01", t: "The Spark", b: "8 teams. 1 court. Sold-out launch — Kenya's first franchise padel tournament." },
  { y: "S02", t: "The Surge", b: "12 teams. 3 courts. Live broadcast pilot. 4× the live audience of Season 01." },
  { y: "S03", t: "The Stage", b: "16 teams. 4 courts. National broadcast & televised final. KES 3.97M prize purse." },
];

const OFFERS = [
  { id: "fans",     icon: Tv,        title: "For Fans",     bullets: ["Live court grid streaming", "Match alerts & schedules", "Player profiles & stats"], cta: { label: "Watch Live", href: "/live" }, accent: "#00A86B" },
  { id: "players",  icon: UserPlus,  title: "For Players",  bullets: ["Open registration · KES 5,000 fee", "Auction draft into a franchise", "Career stats + minutes tracker"], cta: { label: "Register as Player", href: "/register-player" }, accent: "#F4B400" },
  { id: "owners",   icon: Crown,     title: "For Owners",   bullets: ["Build your squad live in the auction", "Permanent franchise rights", "Share of broadcast & prize pool"], cta: { label: "Own a Franchise", scrollTo: "home-franchise" }, accent: "#E32424" },
  { id: "brands",   icon: Megaphone, title: "For Brands",   bullets: ["Jersey · court · broadcast placements", "Title / Gold / Silver / Court tiers", "On-site stalls & activations"], cta: { label: "Become a Sponsor", scrollTo: "home-sponsor" }, accent: "#7DD3FC" },
];

function useCountdown(targetIso) {
  const target = useMemo(() => (targetIso ? new Date(`${targetIso}T18:00:00Z`).getTime() : null), [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, target - now);
  const days  = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins  = Math.floor((diff / (1000 * 60)) % 60);
  const secs  = Math.floor((diff / 1000) % 60);
  return { days, hours, mins, secs };
}

export default function PublicHome() {
  const [cfg, setCfg] = useState(null);
  const [live, setLive] = useState({ matches: [], sponsors: [] });
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [content, setContent] = useState(null);
  const [groupStandings, setGroupStandings] = useState(null);
  const [liveTies, setLiveTies] = useState([]);

  useEffect(() => {
    api.get("/public/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/public/live").then(({ data }) => setLive(data)).catch(() => {});
    api.get("/public/teams").then(({ data }) => setTeams(data || [])).catch(() => {});
    api.get("/public/players/top?limit=8").then(({ data }) => setPlayers(data || [])).catch(() => {});
    api.get("/public/site-content").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/public/ties/live").then(({ data }) => setLiveTies(data || [])).catch(() => {});
  }, []);

  // Refresh ties on set-score/finalized events so live tie cards stay live.
  useEffect(() => {
    let alive = true;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    let es = null;
    const refresh = () => {
      if (!alive) return;
      api.get("/public/ties/live").then(({ data }) => alive && setLiveTies(data || [])).catch(() => {});
    };
    try {
      es = new EventSource(`${base}/api/public/events/stream`);
      es.addEventListener("match.set_scored", refresh);
      es.addEventListener("match.finalized", refresh);
    } catch { /* silent */ }
    // Slow fallback poll for browsers where SSE reconnect is flaky.
    const iv = window.setInterval(refresh, 45000);
    return () => { alive = false; try { es && es.close(); } catch { /* noop */ } window.clearInterval(iv); };
  }, []);

  // Live Group Progress — SSE stream with polling fallback. The stream emits
  // a `snapshot` on connect and an `update` every time a match is finalized.
  useEffect(() => {
    let alive = true;
    let es = null;
    let pollIv = null;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const setFromEvt = (e) => {
      if (!alive) return;
      try {
        const data = JSON.parse(e.data);
        if (data && data.groups) setGroupStandings(data);
      } catch { /* silent */ }
    };
    const startPolling = () => {
      const tick = () => {
        if (!alive || document.visibilityState !== "visible") return;
        api.get("/public/standings/by-group")
          .then(({ data }) => alive && setGroupStandings(data))
          .catch(() => {});
      };
      tick();
      pollIv = window.setInterval(tick, 60000);  // 60s fallback poll
    };
    try {
      es = new EventSource(`${base}/api/public/standings/stream`);
      es.addEventListener("snapshot", setFromEvt);
      es.addEventListener("update", setFromEvt);
      es.onerror = () => {
        // Connection dropped or unsupported — kick off polling as fallback.
        try { es.close(); } catch { /* noop */ }
        if (alive && !pollIv) startPolling();
      };
    } catch {
      startPolling();
    }
    return () => {
      alive = false;
      try { es && es.close(); } catch { /* noop */ }
      if (pollIv) window.clearInterval(pollIv);
    };
  }, []);

  const startDate = cfg?.league_start_date;
  const auctionDate = cfg?.auction_date;
  const countdown = useCountdown(startDate);
  const liveMatches = (live.matches || []).filter((m) => m.status === "live");

  // CMS content with sensible defaults
  const hero = content?.hero || {};
  const recap = content?.season_recap || {};
  const timeline = content?.story_timeline?.length ? content.story_timeline : PAST_HIGHLIGHTS;
  const cmsGallery = content?.gallery || [];
  const cmsSponsors = content?.sponsors || [];
  const cmsAnnouncements = (content?.announcements || []).filter((a) => a.title || a.body);
  const heroBg = hero.background_image || HERO_IMG;
  const recapImg = recap.image || SEASON_IMG;
  const contact = content?.contact || { owners_email: "owners@marapadel.com", partners_email: "partners@marapadel.com", hello_email: "hello@marapadel.com" };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-[#060d09] text-white selection:bg-[#00A86B]/40">
      <PublicNav />
      <LiveScoreTicker />

      {/* ============== EDITORIAL HERO ============== */}
      <section className="relative overflow-hidden border-b border-white/5" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={heroBg} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1611] via-[#0a1611]/95 to-[#1a0606]/70 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060d09] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-14 md:pt-20 pb-16">
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
            <div>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">
                <Flag className="w-3.5 h-3.5" /> <span>{hero.kicker || "Kenya · Season 03"}</span>
                {startDate && <span className="text-zinc-500">· {startDate} → {cfg?.league_end_date}</span>}
              </div>
              <h1
                data-testid="home-hero-title"
                className="font-anton mt-5 text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[110px] uppercase tracking-[-0.04em] leading-[0.85] text-white"
              >
                <span className="block whitespace-nowrap">{hero.line1 || "A League"}</span>
                <span className="block whitespace-nowrap text-[#E32424]">{hero.line2 || "Built Like"}</span>
                <span className="block whitespace-nowrap text-[#00A86B]">{hero.line3 || "A Movement."}</span>
              </h1>
              <KenyaStripe className="mt-6 w-56" />
              <p className="mt-7 text-zinc-300 max-w-2xl text-lg leading-relaxed">
                {hero.subtitle || "The Mara Padel League is Kenya's premier franchise-based padel tournament."}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild data-testid="home-cta-register" className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest px-6 h-12">
                  <Link to="/register-player"><UserPlus className="w-4 h-4 mr-2" /> Register as Player</Link>
                </Button>
                <SponsorInquiryDialog defaultIntent="franchise">
                  <Button data-testid="home-cta-own-team" className="bg-white text-[#0a1611] hover:bg-zinc-200 font-bold uppercase tracking-widest px-6 h-12">
                    <Crown className="w-4 h-4 mr-2" /> Own a Team
                  </Button>
                </SponsorInquiryDialog>
                <SponsorInquiryDialog defaultIntent="sponsorship">
                  <Button data-testid="home-cta-sponsor" className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest px-6 h-12">
                    <Handshake className="w-4 h-4 mr-2" /> Sponsor
                  </Button>
                </SponsorInquiryDialog>
                {liveMatches.length > 0 && (
                  <Button asChild variant="outline" data-testid="home-cta-watch-live" className="border-red-500 bg-red-500/10 text-white hover:bg-red-500/20 font-bold uppercase tracking-widest px-6 h-12">
                    <Link to="/live"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block mr-2" /> Watch Live</Link>
                  </Button>
                )}
              </div>
            </div>

            {/* Editorial side panel — countdown + key stats */}
            <aside className="relative">
              <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur p-6">
                <div className="text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Season 03 Starts In</div>
                {countdown ? (
                  <div className="mt-4 grid grid-cols-4 gap-2" data-testid="home-countdown">
                    <CountTile n={countdown.days}  l="Days" />
                    <CountTile n={countdown.hours} l="Hrs" />
                    <CountTile n={countdown.mins}  l="Min" />
                    <CountTile n={countdown.secs}  l="Sec" />
                  </div>
                ) : (
                  <div className="mt-4 h-20 rounded-lg bg-white/5 animate-pulse" />
                )}
                {auctionDate && (
                  <div className="mt-5 pt-5 border-t border-white/10 text-sm flex items-center justify-between">
                    <span className="text-zinc-400">Live Auction</span>
                    <span className="text-white font-bold">{auctionDate}</span>
                  </div>
                )}
                <div className="mt-3 text-sm flex items-center justify-between">
                  <span className="text-zinc-400">Prize Pool</span>
                  <span className="text-[#00A86B] font-anton text-xl">KES {(cfg?.prize_pool_total || 3970000).toLocaleString()}</span>
                </div>
                <div className="mt-3 text-sm flex items-center justify-between">
                  <span className="text-zinc-400">Franchise Teams</span>
                  <span className="text-white font-anton text-xl">{cfg?.num_teams || 16}</span>
                </div>
              </div>
              <button
                onClick={() => scrollTo("home-offers")}
                className="mt-3 w-full text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-white text-center py-2"
                data-testid="home-scroll-offers"
              >
                ↓ Explore the league
              </button>
            </aside>
          </div>
        </div>
        <div className="kenya-stripe" />
      </section>

      {/* ============== WHAT WE OFFER (audience grid) ============== */}
      <section id="home-offers" className="max-w-7xl mx-auto px-6 py-20 md:py-28" data-testid="home-offers">
        <div className="grid md:grid-cols-[1fr_2fr] gap-10 items-end mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">What We Offer</div>
            <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.9]">
              Four <span className="text-[#00A86B]">Doors.</span><br/>One Arena.
            </h2>
            <KenyaStripe className="mt-4 w-24" />
          </div>
          <p className="text-zinc-300 text-[15px] leading-relaxed">
            Mara Padel is more than a tournament — it&apos;s a layered ecosystem with a clear, valuable role for fans,
            players, team owners, and brand partners. Pick the door that fits you and step in.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-offer-grid">
          {OFFERS.map((o) => {
            const Icon = o.icon;
            return (
              <Card key={o.id} className="bg-[#11241b] border-white/10 p-6 flex flex-col group hover:border-white/30 transition-colors" data-testid={`home-offer-${o.id}`}>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${o.accent}22`, border: `1px solid ${o.accent}66` }}
                >
                  <Icon className="w-6 h-6" style={{ color: o.accent }} />
                </div>
                <div className="font-anton text-2xl uppercase tracking-tight" style={{ color: o.accent }}>{o.title}</div>
                <ul className="mt-4 space-y-2 text-sm text-zinc-300 flex-1">
                  {o.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: o.accent }} /> {b}
                    </li>
                  ))}
                </ul>
                {o.cta.href ? (
                  <Button asChild data-testid={`home-offer-cta-${o.id}`} className="mt-5 text-white font-bold uppercase tracking-widest" style={{ backgroundColor: o.accent }}>
                    <Link to={o.cta.href}>{o.cta.label} <ArrowRight className="w-4 h-4 ml-2" /></Link>
                  </Button>
                ) : (
                  <Button onClick={() => scrollTo(o.cta.scrollTo)} data-testid={`home-offer-cta-${o.id}`} className="mt-5 text-white font-bold uppercase tracking-widest" style={{ backgroundColor: o.accent }}>
                    {o.cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      {/* ============== STORY / PAST SEASONS ============== */}
      <section id="home-story" className="border-y border-white/5 bg-[#0a1611]/60" data-testid="home-story">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[3fr_2fr] gap-10 items-center">
          <div className="relative rounded-2xl overflow-hidden border-2 border-[#00A86B]/30 min-h-[420px]">
            <img src={recapImg} alt={recap.alt || "Mara Padel League — Season 02"} className="absolute inset-0 w-full h-full object-cover" data-testid="home-season-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute top-4 left-4 bg-[#E32424] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full">Last Season</div>
            <div className="absolute bottom-0 left-0 right-0 p-7">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">{recap.label || "Season 02 · Recap"}</div>
              <div className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-1 leading-[0.9]" data-testid="home-season-title">
                {recap.title || "Sold-out stands. Unforgettable nights."}
              </div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">Our Story · So Far</div>
            <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.9]">
              From <span className="text-[#00A86B]">Pilot Court</span><br/>To National Stage.
            </h2>
            <div className="mt-6 space-y-3">
              {timeline.map((h, i) => (
                <div key={h.year || `s-${i}`} className="flex gap-4 items-start" data-testid={`home-history-${h.year}`}>
                  <div className="font-anton text-3xl text-[#00A86B] w-12 shrink-0 leading-none">{h.year}</div>
                  <div className="border-l border-white/10 pl-4">
                    <div className="font-bold uppercase tracking-wide text-white">{h.title}</div>
                    <div className="text-sm text-zinc-400 mt-0.5">{h.blurb}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== TEAMS ============== */}
      <section id="home-teams" className="max-w-7xl mx-auto px-6 py-20 md:py-24" data-testid="home-teams">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">The Franchises</div>
            <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.9]">
              16 <span className="text-[#E32424]">Teams.</span> One Crown.
            </h2>
          </div>
          <SponsorInquiryDialog defaultIntent="franchise">
            <Button variant="outline" className="border-[#E32424]/50 text-white hover:bg-[#E32424]/10" data-testid="home-teams-cta-own">
              <Crown className="w-4 h-4 mr-2" /> Own a Team <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </SponsorInquiryDialog>
        </div>

        {teams.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="home-teams-grid">
            {teams.map((t) => {
              const card = (
                <>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2 font-anton text-2xl text-white" style={{ background: t.color || "#16322a", border: "2px solid rgba(255,255,255,0.1)" }}>
                    {(t.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="font-anton text-sm uppercase tracking-tight truncate w-full text-white">{t.name}</div>
                  {t.owner_name && <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 truncate w-full">{t.owner_name}</div>}
                </>
              );
              return (
                <Link key={t.id} to={`/teams/${t.id}`} data-testid={`home-team-${t.id}`} className="bg-[#11241b] border border-white/10 rounded-lg p-4 flex flex-col items-center text-center hover:border-[#00A86B]/40 transition-colors">
                  {card}
                </Link>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={Crown}
            title="Franchise Slots Open"
            subtitle="Season 03 is welcoming new team owners. Be one of the founding 16."
            cta={{ component: (
              <SponsorInquiryDialog defaultIntent="franchise">
                <Button className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest" data-testid="home-teams-empty-cta">Apply to Own a Team</Button>
              </SponsorInquiryDialog>
            )}}
            testid="home-teams-empty"
          />
        )}
      </section>

      {/* ============== TOP PLAYERS ============== */}
      <section id="home-players" className="bg-[#0a1611]/60 border-y border-white/5" data-testid="home-players">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400] font-bold">The Talent</div>
              <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.9]">
                Top <span className="text-[#F4B400]">Players</span> · Season 03 Pool.
              </h2>
            </div>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="home-players-cta-register">
              <Link to="/register-player"><UserPlus className="w-4 h-4 mr-2" /> Join the Pool</Link>
            </Button>
          </div>

          {players.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-players-grid">
              {players.map((p) => (
                <Card key={p.id} className="relative overflow-hidden bg-[#11241b] border-white/10 hover:border-[#F4B400]/50 transition-colors group" data-testid={`home-player-${p.id}`}>
                  <div className="aspect-[3/4] relative">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#16322a] to-[#06210f]">
                        <span className="font-anton text-6xl text-[#F4B400]/40">{p.name?.slice(0,1) || "?"}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute top-3 left-3 flex flex-col gap-1">
                      <span className="text-[10px] uppercase tracking-widest bg-[#F4B400] text-black px-2 py-0.5 rounded font-bold">{p.tier || "—"}</span>
                      {p.is_international && <span className="text-[10px] uppercase tracking-widest bg-[#E32424] text-white px-2 py-0.5 rounded font-bold flex items-center gap-1"><Star className="w-3 h-3" /> INTL</span>}
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="font-anton text-xl uppercase tracking-tight text-white truncate">{p.name}</div>
                      <div className="text-xs text-zinc-300 flex items-center gap-2">
                        <span className="capitalize">{p.category}</span>
                        <span>·</span>
                        <span className="text-[#F4B400] font-bold">{p.rating}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Users2}
              title="The Roster Is Forming"
              subtitle="Be one of the first names called at the Season 03 auction. Registrations are open."
              cta={{ component: (
                <Button asChild className="bg-[#F4B400] hover:bg-[#d99e00] text-black font-bold uppercase tracking-widest">
                  <Link to="/register-player" data-testid="home-players-empty-cta">Register as Player <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
              )}}
              testid="home-players-empty"
            />
          )}
        </div>
      </section>

      {/* ============== FIXTURES / LIVE ============== */}
      <section id="home-fixtures" className="max-w-7xl mx-auto px-6 py-20 md:py-24" data-testid="home-fixtures">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#00A86B] font-bold flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> The Schedule</div>
            <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.9]">
              {liveMatches.length > 0 ? <>Live <span className="text-red-500">Right Now.</span></> : <>The <span className="text-[#00A86B]">Fixtures.</span></>}
            </h2>
          </div>
          <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="home-fixtures-cta-live">
            <Link to="/live"><Tv className="w-4 h-4 mr-2" /> Full Live Feed</Link>
          </Button>
        </div>

        <FixturesPanel matches={live.matches} />
      </section>

      {/* ============== LIVE GROUP PROGRESS ============== */}
      <GroupProgressStrip data={groupStandings} />

      {/* ============== HIGHLIGHTS AUTO-REEL ============== */}
      <section className="max-w-7xl mx-auto px-6 pb-6" data-testid="home-highlights-section">
        <HighlightsReel />
      </section>

      {/* ============== LIVE TEAM TIES (3-seed) ============== */}
      <LiveTiesStrip ties={liveTies} />

      {/* ============== OWN A FRANCHISE ============== */}
      <section id="home-franchise" className="max-w-7xl mx-auto px-6 pb-10" data-testid="home-franchise">
        <div className="rounded-2xl overflow-hidden relative border border-[#E32424]/30">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1611] via-[#1a0606] to-[#BB0000]" />
          <div className="relative p-8 md:p-12 grid lg:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">
                <Crown className="w-3.5 h-3.5" /> Franchise Ownership · 16 Slots Open
              </div>
              <h3 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-2 leading-[0.95]">
                Own a <span className="text-[#E32424]">Mara Padel</span> Franchise.
              </h3>
              <p className="mt-4 text-zinc-300 text-[15px] leading-relaxed max-w-2xl">
                Become a Team Owner in Kenya&apos;s most ambitious franchise league. Build your roster live from the auction pool,
                captain your brand on national broadcast, and compete for the KES 3.97M prize pool.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-zinc-200">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Pick your 7-player squad via live auction</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Permanent franchise rights · future seasons</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Team jersey, branding & social packages</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Share of prize-pool earnings + visibility</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <SponsorInquiryDialog defaultIntent="franchise">
                <Button data-testid="home-franchise-cta" className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest h-12 w-full">
                  <Crown className="w-4 h-4 mr-2" /> Apply to Own a Team
                </Button>
              </SponsorInquiryDialog>
              <a href="mailto:owners@marapadel.com" data-testid="home-franchise-email" className="text-center text-xs text-zinc-400 hover:text-white">
                or email <span className="underline">owners@marapadel.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============== SPONSOR ============== */}
      <section id="home-sponsor" className="max-w-7xl mx-auto px-6 pb-20" data-testid="home-sponsor">
        <div className="rounded-2xl overflow-hidden relative border border-[#00A86B]/30">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1611] via-[#06210f] to-[#006B3F]" />
          <div className="relative p-8 md:p-12 grid lg:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">
                <Megaphone className="w-3.5 h-3.5" /> Brand Partnerships · Open
              </div>
              <h3 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-2 leading-[0.95]">
                Sponsor a <span className="text-[#00A86B]">Team</span> or the League.
              </h3>
              <p className="mt-4 text-zinc-300 text-[15px] leading-relaxed max-w-2xl">
                Put your brand on the jersey, the court, or the broadcast. Title, Gold, Silver and Court tiers — built to reach
                Kenya&apos;s most affluent, sports-engaged audience live and across our digital channels.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-zinc-200">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Jersey placements (chest · sleeve · back)</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Court & glass-wall branding zones</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Title, Gold, Silver & Court tiers</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Stalls + on-site activation slots</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <SponsorInquiryDialog defaultIntent="sponsorship">
                <Button data-testid="home-sponsor-cta" className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest h-12 w-full">
                  <Handshake className="w-4 h-4 mr-2" /> Become a Sponsor
                </Button>
              </SponsorInquiryDialog>
              <a href="mailto:partners@marapadel.com" data-testid="home-sponsor-email" className="text-center text-xs text-zinc-400 hover:text-white">
                or email <span className="underline">partners@marapadel.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============== ANNOUNCEMENTS ============== */}
      {cmsAnnouncements.length > 0 && (
        <section className="border-t border-[#F4B400]/20 bg-[#1a1306]/60" data-testid="home-announcements">
          <div className="max-w-7xl mx-auto px-6 py-12 md:py-14">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400] font-bold mb-4">Announcements</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cmsAnnouncements.slice(0, 6).map((a, i) => (
                <Card key={a.id || `a-${i}`} className="bg-[#1a1306] border-[#F4B400]/20 p-5" data-testid={`home-announcement-${a.id || i}`}>
                  {a.pinned && <span className="text-[10px] uppercase tracking-widest bg-[#F4B400] text-black px-2 py-0.5 rounded font-bold inline-block mb-2">Pinned</span>}
                  <div className="font-anton text-xl uppercase tracking-tight text-white">{a.title}</div>
                  <p className="text-sm text-zinc-300 mt-2 whitespace-pre-line leading-relaxed">{a.body}</p>
                  {a.published_at && <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3">{a.published_at}</div>}
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== GALLERY ============== */}
      {cmsGallery.length > 0 && (
        <section className="border-t border-white/5" data-testid="home-gallery">
          <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">Gallery</div>
            <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter mt-2 leading-[0.9] text-white">Through The <span className="text-[#00A86B]">Lens.</span></h2>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {cmsGallery.map((g, i) => (
                <div key={g.id || `g-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 group" data-testid={`home-gallery-${g.id || i}`}>
                  <img src={g.image_url} alt={g.caption || ""} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {g.caption && (
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black via-black/60 to-transparent">
                      <div className="text-xs text-white">{g.caption}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== SPONSOR STRIP ============== */}
      {(cmsSponsors.length > 0 || (live.sponsors && live.sponsors.length > 0)) && (
        <section className="border-t border-white/5 py-12" data-testid="home-sponsor-strip">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 text-center mb-6">Powered by our sponsors</div>
            {cmsSponsors.length > 0 ? (
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
                {cmsSponsors.map((s, i) => {
                  const key = s.id || `s-${i}`;
                  const inner = s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="h-12 md:h-14 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity bg-white/95 rounded px-3 py-1.5" />
                  ) : (
                    <div className="font-anton text-xl uppercase text-zinc-400 hover:text-white transition-colors">{s.name}</div>
                  );
                  return s.website ? (
                    <a key={key} href={s.website} target="_blank" rel="noopener noreferrer" data-testid={`home-sponsor-${key}`}>{inner}</a>
                  ) : (
                    <div key={key} data-testid={`home-sponsor-${key}`}>{inner}</div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
                {live.sponsors.map((s) => (
                  <div key={s.id} className="font-anton text-xl uppercase text-zinc-400 hover:text-white transition-colors" data-testid={`home-sponsor-${s.id}`}>{s.name}</div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="kenya-stripe" />
      <PublicFooter contact={contact} />
    </div>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */
function PublicNav() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#060d09]/85 border-b border-white/5" data-testid="home-nav">
      <div className="kenya-stripe" />
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="shrink-0"><Logo variant="inline" className="h-10" /></Link>
        <nav className="hidden md:flex items-center gap-6 text-sm uppercase tracking-widest text-zinc-400">
          <a href="#home-offers" className="hover:text-white">What We Offer</a>
          <a href="#home-story" className="hover:text-white">Our Story</a>
          <a href="#home-teams" className="hover:text-white">Teams</a>
          <Link to="/players-directory" className="hover:text-white">Players</Link>
          <Link to="/standings" className="hover:text-white">Standings</Link>
          <Link to="/live" className="hover:text-white">Live</Link>
          <a href="#home-franchise" className="hover:text-[#E32424]">Own</a>
          <a href="#home-sponsor" className="hover:text-[#00A86B]">Sponsor</a>
        </nav>
        <div className="flex items-center gap-2">
          <InstallAppButton testid="home-nav-install" compact />
          <Button asChild size="sm" className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest" data-testid="home-nav-register">
            <Link to="/register-player">Register</Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="home-nav-admin">
            <Link to="/admin"><LogIn className="w-3.5 h-3.5 mr-1.5" /> Admin</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

function PublicFooter({ contact }) {
  const c = contact || { owners_email: "owners@marapadel.com", partners_email: "partners@marapadel.com", hello_email: "hello@marapadel.com" };
  return (
    <footer className="bg-[#0a1611] py-10 text-xs text-zinc-500" data-testid="home-footer">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6 items-start">
        <div>
          <Logo variant="inline" className="h-9" />
          <p className="mt-3 text-zinc-500 max-w-xs">Kenya&apos;s premier franchise-based padel league. Three seasons. One movement.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">Navigate</div>
            <div className="flex flex-col gap-1.5">
              <Link to="/live" className="hover:text-white">Live Broadcast</Link>
              <Link to="/register-player" className="hover:text-white">Register as Player</Link>
              <Link to="/admin" className="hover:text-white">Operator Sign-in</Link>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">Contact</div>
            <div className="flex flex-col gap-1.5">
              <a href={`mailto:${c.owners_email}`} className="hover:text-white">{c.owners_email}</a>
              <a href={`mailto:${c.partners_email}`} className="hover:text-white">{c.partners_email}</a>
              <a href={`mailto:${c.hello_email}`} className="hover:text-white">{c.hello_email}</a>
            </div>
          </div>
        </div>
        <div className="text-right md:text-right">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">© {new Date().getFullYear()} Mara Padel League</div>
          <div>Season 03 · Kenya</div>
        </div>
      </div>
    </footer>
  );
}

function CountTile({ n, l }) {
  const v = String(n).padStart(2, "0");
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
      <div className="font-anton text-3xl md:text-4xl text-white leading-none tabular-nums">{v}</div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{l}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle, cta, testid }) {
  return (
    <Card className="p-10 text-center bg-[#11241b] border-white/10 border-dashed" data-testid={testid}>
      <Icon className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
      <div className="font-anton text-2xl md:text-3xl uppercase text-white tracking-tight">{title}</div>
      <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">{subtitle}</p>
      {cta?.component && <div className="mt-5 flex justify-center">{cta.component}</div>}
    </Card>
  );
}

function LiveTiesStrip({ ties }) {
  if (!ties || ties.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 pb-16 md:pb-20" data-testid="home-live-ties">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-red-400 font-bold flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Team Ties On Court
          </div>
          <h2 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter mt-2 leading-[0.9]">
            3 Courts. <span className="text-[#F4B400]">1 Tie.</span>
          </h2>
          <div className="text-xs text-zinc-500 mt-1">
            Every team tie = Seed 1 · Seed 2 · Seed 3 · aggregated games decide the winner.
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {ties.slice(0, 4).map(t => <TieCard key={t.tie_id} tie={t} />)}
      </div>
    </section>
  );
}

function TieCard({ tie }) {
  const s = (tie.status || "scheduled").toLowerCase();
  const badge = s === "live"
    ? { label: "🔴 LIVE", cl: "bg-red-500/20 text-red-300 border-red-500/40 animate-pulse" }
    : s === "finished"
    ? { label: "FINAL", cl: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" }
    : { label: "SCHEDULED", cl: "bg-white/10 text-white/70 border-white/10" };
  const gd = tie.game_difference || 0;
  return (
    <Card className="bg-[#0d1a13] border-white/10 overflow-hidden" data-testid={`tie-card-${tie.tie_id}`}>
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap border-b border-white/10">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{(tie.matches?.[0]?.stage || "").toUpperCase()}</div>
          <div className="font-anton text-2xl uppercase text-white truncate">
            <Link to={`/teams/${tie.team_a?.id}`} className="hover:text-[#F4B400]">{tie.team_a?.name || "Team A"}</Link>
            <span className="text-zinc-500 mx-1">vs</span>
            <Link to={`/teams/${tie.team_b?.id}`} className="hover:text-[#F4B400]">{tie.team_b?.name || "Team B"}</Link>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${badge.cl}`}>{badge.label}</span>
      </div>
      <div className="px-4 py-3 flex items-center gap-4 border-b border-white/5">
        <div className="flex-1 text-center">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{tie.team_a?.name || "A"}</div>
          <div className="font-anton text-4xl text-white leading-none mt-1">{tie.total_games_a || 0}</div>
        </div>
        <div className="text-zinc-500 text-2xl font-anton">—</div>
        <div className="flex-1 text-center">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{tie.team_b?.name || "B"}</div>
          <div className="font-anton text-4xl text-white leading-none mt-1">{tie.total_games_b || 0}</div>
        </div>
        <div className="pl-3 border-l border-white/10 text-center">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">GD</div>
          <div className={`font-anton text-3xl leading-none mt-1 ${gd > 0 ? "text-emerald-300" : gd < 0 ? "text-red-300" : "text-white"}`}>
            {gd > 0 ? "+" : ""}{gd}
          </div>
        </div>
      </div>
      <div className="p-2 space-y-1">
        {(tie.matches || []).map((m, idx) => (
          <TieSeedRow key={m.id} m={m} matchNumber={idx + 1} tieId={tie.tie_id} />
        ))}
      </div>
    </Card>
  );
}

function TieSeedRow({ m, matchNumber, tieId }) {
  const s = (m.status || "scheduled").toLowerCase();
  const isSet = m.format_type === "set_based";
  const scoreStr = isSet
    ? ((m.set_scores || []).filter(x => x.a || x.b).map(x => `${x.a}-${x.b}`).join(", ") || "—")
    : `${m.score_a || 0} : ${m.score_b || 0}`;
  const statusLabel = s === "live" ? "LIVE" : s === "finished" ? "FINAL" : "TBD";
  const statusColor = s === "live" ? "text-red-300" : s === "finished" ? "text-emerald-300" : "text-zinc-500";
  // Pair labels only appear once BOTH lineups are revealed & locked.
  const revealed = m.lineups_revealed_at || (m.lineup_a_players && m.lineup_b_players);
  const pairA = revealed ? (m.lineup_a_players || []).map(p => p.short_name || p.name).join(" / ") : "";
  const pairB = revealed ? (m.lineup_b_players || []).map(p => p.short_name || p.name).join(" / ") : "";
  return (
    <Link to={`/matches/${m.id}`} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/[0.03] transition-colors" data-testid={`tie-seed-${m.id}`}>
      <div className="w-16">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">Match {matchNumber}</div>
        <div className="font-anton text-lg text-[#F4B400] leading-none">Seed {m.seed || "?"}</div>
      </div>
      <div className="flex-1 text-sm text-white truncate">
        {revealed && (pairA || pairB) ? (
          <span className="text-zinc-300 text-[11px] truncate block" data-testid={`tie-seed-${m.id}-pairs`}>
            <span className="text-white/90">{pairA || "—"}</span>
            <span className="text-zinc-600 mx-1">vs</span>
            <span className="text-white/90">{pairB || "—"}</span>
          </span>
        ) : (
          <span className="text-zinc-500 text-[10px] uppercase tracking-widest" data-testid={`tie-seed-${m.id}-labels`}>
            Seed {m.seed} vs Seed {m.seed}
          </span>
        )}
        <span className="text-zinc-400 text-[10px] uppercase tracking-widest mr-2">{m.court}</span>
        {scoreStr}
      </div>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${statusColor}`}>
        {s === "live" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5 animate-pulse" />}
        {statusLabel}
      </div>
    </Link>
  );
}

function GroupProgressStrip({ data }) {
  // Ordered as A / B / C, hide any group with 0 teams so we never show a lonely "?"
  const groups = data?.groups || {};
  const nonEmptyKeys = ["A", "B", "C"].filter((k) => (groups[k] || []).length > 0);
  const wildCards = data?.wild_cards || [];
  if (nonEmptyKeys.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 pb-16 md:pb-20" data-testid="home-group-progress">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400] font-bold flex items-center gap-2">
            <Activity className="w-3.5 h-3.5" /> Live Group Progress
          </div>
          <h2 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter mt-2 leading-[0.9]">
            The <span className="text-[#F4B400]">Race</span> to Top 2.
          </h2>
          <div className="text-xs text-zinc-500 mt-1">
            Ranked by <b className="text-white/80">Game Difference</b> · Live via SSE · {data?.finished_matches || 0} matches counted
          </div>
        </div>
        <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="home-groups-cta-full">
          <Link to="/standings"><ArrowUpRight className="w-4 h-4 mr-2" /> Full Standings</Link>
        </Button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {nonEmptyKeys.map((k) => (
          <GroupTable key={k} groupKey={k} rows={groups[k]} />
        ))}
      </div>

      {wildCards.length > 0 && (
        <div className="mt-6 p-4 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06]" data-testid="wildcard-strip">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">Wild Card Race</span>
            <span className="text-[11px] text-zinc-400">Top-2 non-auto-qualifiers by <b>Average Game Difference</b> earn the last QF slots</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {wildCards.map((w, i) => (
              <div key={w.team_id} className="flex items-center gap-3 bg-black/30 rounded-lg px-3 py-2" data-testid={`wildcard-${i + 1}`}>
                <div className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-400/50 flex items-center justify-center text-[11px] font-anton text-amber-200">{i + 1}</div>
                {w.logo_url ? (
                  <img src={w.logo_url} alt={w.team_name} className="w-7 h-7 rounded object-contain bg-black/40" />
                ) : (
                  <div className="w-7 h-7 rounded bg-black/40 border border-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{w.team_name}</div>
                  <div className="text-[10px] text-zinc-500">Group {w.group} · GD {w.game_diff >= 0 ? "+" : ""}{w.game_diff} · Avg {w.avg_game_diff.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function GroupTable({ groupKey, rows }) {
  return (
    <Card className="bg-[#0d1a13] border-white/10 overflow-hidden" data-testid={`group-table-${groupKey}`}>
      <div className="px-4 py-3 flex items-center gap-2 bg-gradient-to-r from-[#11241b] to-[#0d1a13] border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-[#F4B400]/15 border border-[#F4B400]/40 flex items-center justify-center font-anton text-lg text-[#F4B400]">
          {groupKey}
        </div>
        <div className="font-anton text-lg uppercase tracking-widest text-white">Group {groupKey}</div>
        <span className="ml-auto text-[10px] text-zinc-500 uppercase tracking-widest">{rows.length} teams</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
            <th className="text-left px-3 py-2 w-6">#</th>
            <th className="text-left px-3 py-2">Team</th>
            <th className="text-right px-2 py-2 w-8" title="Team ties played">P</th>
            <th className="text-right px-2 py-2 w-9" title="Games Won across all matches">GW</th>
            <th className="text-right px-2 py-2 w-9" title="Games Lost across all matches">GL</th>
            <th className="text-right px-3 py-2 w-10" title="Game Difference">GD</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const qualifies = i < 2;
            const gd = r.game_diff ?? (r.for - r.against);
            return (
              <tr
                key={r.team_id}
                className={`border-b border-white/5 ${qualifies ? "bg-emerald-500/[0.06]" : ""}`}
                data-testid={`group-row-${groupKey}-${i + 1}`}
              >
                <td className="px-3 py-2 text-zinc-500 tabular-nums">{i + 1}</td>
                <td className="px-3 py-2">
                  <Link
                    to={`/teams/${r.team_id}`}
                    className="flex items-center gap-2 text-white hover:text-[#F4B400]"
                    data-testid={`group-row-link-${r.team_id}`}
                  >
                    {r.logo_url ? (
                      <img src={r.logo_url} alt={r.team_name} className="w-6 h-6 rounded object-contain bg-black/40" />
                    ) : (
                      <div className="w-6 h-6 rounded bg-black/40 border border-white/10" />
                    )}
                    <span className="truncate max-w-[130px]" title={r.team_name}>{r.team_name}</span>
                    {qualifies && (
                      <span className="ml-1 w-1.5 h-1.5 rounded-full bg-emerald-400" title="Auto-qualifier zone" />
                    )}
                  </Link>
                </td>
                <td className="px-2 py-2 text-right text-zinc-300 tabular-nums">{r.played}</td>
                <td className="px-2 py-2 text-right text-emerald-300 tabular-nums">{r.for}</td>
                <td className="px-2 py-2 text-right text-red-300 tabular-nums">{r.against}</td>
                <td className={`px-3 py-2 text-right font-anton text-lg tabular-nums ${gd > 0 ? "text-emerald-300" : gd < 0 ? "text-red-300" : "text-white"}`}>
                  {gd > 0 ? "+" : ""}{gd}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-xs text-zinc-500 py-4">No matches counted yet.</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[10px] text-zinc-500 border-t border-white/5 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Top 2 auto-qualify · No points, ranked by GD
      </div>
    </Card>
  );
}

function FixturesPanel({ matches = [] }) {  const live = matches.filter((m) => m.status === "live");
  const upcoming = matches.filter((m) => m.status === "scheduled").slice(0, 6);
  const finished = matches.filter((m) => m.status === "finished").slice(-4);

  if (live.length === 0 && upcoming.length === 0 && finished.length === 0) {
    return (
      <EmptyState
        icon={Calendar}
        title="Fixtures Drop Soon"
        subtitle="The Season 03 schedule will be released right after the auction. Bookmark this page or follow the live broadcast."
        cta={{ component: (
          <Button asChild className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
            <Link to="/live" data-testid="home-fixtures-empty-cta">Open Live Feed <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        )}}
        testid="home-fixtures-empty"
      />
    );
  }

  return (
    <div className="space-y-6">
      {live.length > 0 && (
        <FixturesGroup title="Live On Court" tone="live" matches={live} testid="home-fixtures-live" />
      )}
      {upcoming.length > 0 && (
        <FixturesGroup title="Up Next" tone="next" matches={upcoming} testid="home-fixtures-next" />
      )}
      {finished.length > 0 && (
        <FixturesGroup title="Recent Results" tone="done" matches={finished} testid="home-fixtures-done" />
      )}
    </div>
  );
}

function FixturesGroup({ title, tone, matches, testid }) {
  const dot = tone === "live" ? "bg-red-500 animate-pulse" : tone === "next" ? "bg-[#00A86B]" : "bg-zinc-500";
  return (
    <div data-testid={testid}>
      <div className="font-anton text-2xl uppercase tracking-tight text-white mb-3 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full inline-block ${dot}`} />
        {title}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {matches.map((m) => (
          <Card key={m.id} className="bg-[#11241b] border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between mb-2">
              <span>
                {m.court} · {m.stage}
                {m.seed ? ` · Seed ${m.seed}` : ""}
              </span>
              {tone === "live" && (
                <span className="px-2 py-0.5 rounded-full bg-red-600 text-white">LIVE</span>
              )}
              {tone === "done" && <span>FINAL</span>}
            </div>
            {tone === "next" ? (
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-anton text-lg uppercase text-white truncate">
                  {m.team_a?.name || m.team_a_name || "—"}
                </div>
                <span className="text-zinc-600 text-xs">vs</span>
                <div className="font-anton text-lg uppercase text-white text-right truncate">
                  {m.team_b?.name || m.team_b_name || "—"}
                </div>
              </div>
            ) : tone === "live" ? (
              <PadelLiveScore m={m} />
            ) : (
              <PadelFinalScore m={m} />
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
